import { message } from 'antd';
import { isAxiosError } from 'axios';
import { debounce, put, type Effect, type ForkEffect, takeEvery, select } from 'redux-saga/effects';
import * as Effects from 'redux-saga/effects';
import { replace } from 'redux-first-history';
import type { PayloadAction } from '@reduxjs/toolkit';

import * as Api from '~/api';
import { getWithParamString } from '~/helpers';

import { Columns } from '~/components/SourcePhoneNumbers/Columns';

import type { SourcePhoneNumber } from '~/types/models';
import { type ErrorResponse, type IndexResponse, isErrorResponse } from '~/types/responses';
import type { IRestIndexParams } from '~/types/states';

import { actions as app } from '~/redux/app/slice';
import { watchHandleError } from '~/redux/app/saga';

import { actions } from './slice';
import { getItem, getParams } from './selectors';

const call: any = Effects.call;

export function* watchCreate({ payload: item }: PayloadAction<Partial<SourcePhoneNumber>>): Generator<Effect, void, SourcePhoneNumber> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));

  try {
    yield put(app.title('Création...'));

    const newItem = yield call(Api.create, '/source_phone_numbers', item);
    message.success('Numéro de téléphone source créé avec succès.');

    yield call(Api.clearCache, /.*source_phone_numbers.*/);

    yield put(replace(`/configuration/source_phone_numbers/${newItem.humanId}/edit`));
  } catch (e) {
    yield put(app.title('Nouveau numéro de téléphone source'));
    yield put(actions.setLoading(false));

    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  }
}

export function* watchDestroy({ payload: { id } }: PayloadAction<SourcePhoneNumber>): Generator<Effect, void, void> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));
  try {
    yield call(Api.destroy, '/source_phone_numbers', id);
    message.success('Numéro de téléphone source supprimé avec succès.');

    yield call(watchIndex, {});
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchIndex({
  payload: params,
}: PayloadAction<IRestIndexParams<SourcePhoneNumber>>): Generator<Effect, void, IndexResponse<SourcePhoneNumber>> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));

  try {
    const currentParams = yield select(getParams);
    const { currentPage, items, pageSize, total, totalPages } = yield call(Api.index, '/source_phone_numbers', {
      ...currentParams,
      ...params,
    });
    yield put(actions.setPage(currentPage));
    yield put(actions.setData(items));
    yield put(actions.setPageSize(pageSize));
    yield put(actions.setTotal(total));
    yield put(actions.setTotalPages(totalPages));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchRetrieve({
  payload: { id, params },
}: PayloadAction<{ id: SourcePhoneNumber['id'] | SourcePhoneNumber['humanId']; params?: IRestIndexParams<SourcePhoneNumber> }>): Generator<Effect, void, SourcePhoneNumber> {
  try {
    yield put(actions.setLoading(true));
    yield put(actions.setError(null));
    let item = yield select(getItem);
    if (item?.humanId !== id) {
      yield put(app.title('Chargement...'));
      yield put(actions.setItem(null));
      yield put(actions.setInitialItem(null));

      item = yield call(Api.retrieve, `/source_phone_numbers/${id}`, {
        ...(params ?? {}),
        with: getWithParamString(Columns, 'single'),
      });
      yield put(actions.setItem(item));
      yield put(actions.setInitialItem(JSON.stringify(item)));
    }
    yield put(app.title(item.number));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchSetParams({
  payload: params,
}: PayloadAction<IRestIndexParams<SourcePhoneNumber>>): Generator<Effect, void, SourcePhoneNumber> {
  const { sortBy, page, pageSize, ...filters } = params;

  yield put(actions.setPage(page));
  yield put(actions.setPageSize(pageSize));
  yield put(actions.setSortBy(sortBy));
  yield put(actions.setFilters(filters));
}

export function* watchUpdate({ payload: item }: PayloadAction<SourcePhoneNumber>): Generator<Effect, void, SourcePhoneNumber> {
  if (!item.id) {
    return;
  }

  yield put(actions.setLoading(true));
  yield put(app.title('Sauvegarde...'));
  yield put(actions.setError(null));

  try {
    yield call(Api.update, `/source_phone_numbers/${item.id}`, item);
    const updatedItem = yield call(Api.retrieve, `/source_phone_numbers/${item.id}`, {
      with: getWithParamString(Columns, 'single'),
    });
    yield put(actions.setItem(updatedItem));
    yield put(actions.setInitialItem(JSON.stringify(updatedItem)));
    message.success('Numéro de téléphone source mis à jour avec succès.');

    yield put(app.title(updatedItem.number));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(app.title(item.number));
    yield put(actions.setLoading(false));
  }
}

export function* watchSourcePhoneNumbersSagas(): Generator<ForkEffect, void> {
  yield debounce(500, actions.index, watchIndex);
  yield debounce(100, actions.retrieve, watchRetrieve);
  yield takeEvery(actions.create, watchCreate);
  yield takeEvery(actions.destroy, watchDestroy);
  yield takeEvery(actions.setParams, watchSetParams);
  yield takeEvery(actions.update, watchUpdate);
}

const sourceEmailsSagas = watchSourcePhoneNumbersSagas;

export default sourceEmailsSagas;
